/**
 * The stack builder's sequence model: what the screen loads from `sequences/sequenceView/{id}` and
 * what it sends back.
 *
 * The payloads are byte-for-byte what the classic editor (views/Sequences/SequenceDetails) sends, so
 * a sequence saved here opens unchanged there and the backend needs nothing new. Where the classic
 * editor does something odd — sending the head offers-sequence without its id, dropping a trailing
 * return-URL row on load and re-appending it on save — this does the same, on purpose. Nothing here
 * imports from the classic folder, so that folder can be deleted once this editor has replaced it.
 */
import { buildCriteriaPayload, handleCriteriaInitialize, handleCriteriaSubmit } from '../../../utils/CriteriaHelpers';
import { processCustomVariables } from '../../../components/CustomVariables/utils';

/* eslint-disable @typescript-eslint/no-explicit-any -- these mirror loosely-typed API entities */
export type Ref = { id: number; name?: string; [key: string]: any };
export type Offer = {
  id: number;
  name: string;
  isActive?: boolean;
  isPrimaryVariant?: boolean | null;
  offerType?: { id: number; name: string } | null;
  advertiser?: { id: number; name: string } | null;
  offerBlock?: { id: number; offerBlockOffers?: { offer?: Offer }[] } | null;
  offerActionId?: number | null;
  offerActionType?: string | null;
  [key: string]: any;
};

/** One row in the stack. `offersSequence` is the saved row this came from, or null for a new one. */
export type StackItem = {
  key: string;
  offer: Offer;
  offersSequence: Record<string, any> | null;
  layoutOverride: Ref | null;
  containerOverride: Ref | null;
};

/** The offer types a sequence has a default layout and container for, and the form fields that hold them. */
export const TEMPLATE_DEFAULTS = [
  { offerType: 'Form', layoutField: 'defaultFormLayout', containerField: 'defaultFormContainer' },
  { offerType: 'Banner', layoutField: 'defaultBannerLayout', containerField: 'defaultBannerContainer' },
  { offerType: 'Question', layoutField: 'defaultQuestionLayout', containerField: 'defaultQuestionContainer' },
  { offerType: 'Offer Wall', layoutField: 'defaultOfferWallLayout', containerField: 'defaultOfferWallContainer' },
  { offerType: 'Iframe', layoutField: 'defaultIframeLayout', containerField: 'defaultIframeContainer' },
] as const;

/** Return URL (6) and Landing Page (8) offers never go into a sequence. */
export const EXCLUDED_OFFER_TYPE_IDS = [6, 8];
const OFFER_BLOCK_TYPE_ID = 9;
const DYNAMIC_OFFER_SECTION_TYPE_ID = 10;
/** The trailing return-URL row carries this orderBy; it is held aside rather than shown. */
const RETURN_URL_ORDER_BY = 9999;

const toRef = (entity: Ref | null | undefined) => (entity ? { id: entity.id, name: entity.name } : null);

let keySeed = 0;
export const newStackKey = () => `new-${Date.now().toString(36)}-${(keySeed++).toString(36)}`;

export const createStackItem = (offer: Offer): StackItem => ({
  key: newStackKey(),
  offer,
  offersSequence: null,
  layoutOverride: null,
  containerOverride: null,
});

/**
 * Which saved rows are shown. A block's member offers are saved as rows of their own (with
 * `offerBlock` set) behind the block's wrapper offer, and an offer's non-primary variations ride
 * on its primary; neither is placed directly, so neither is a row in the stack.
 */
const isPlacedRow = (os: any) =>
  (os?.offer?.isPrimaryVariant !== false && os?.offerBlock === null) ||
  os?.offer?.offerType?.id === OFFER_BLOCK_TYPE_ID ||
  os?.offer?.offerType?.id === DYNAMIC_OFFER_SECTION_TYPE_ID;

export type LoadedSequence = {
  values: Record<string, any>;
  /** The saved template-settings rows by offer type name, so a save updates them instead of adding more. */
  sdtsByType: Record<string, any>;
  /** The trailing return-URL row, re-attached on save. */
  returnUrlOfferSequence: { id: number; orderBy: number } | null;
  sequence: Record<string, any>;
};

/** A new sequence. The classic editor starts from nothing at all; flex and priority of 1 save a step. */
export const emptySequenceValues = (): Record<string, any> => ({
  sequenceName: '',
  site: null,
  flex: 1,
  priority: 1,
  targetReturningUsers: false,
  returnVisits: 0,
  sequenceDescription: null,
  customVariables: null,
  propertySetsIds: [],
  propertySetsValues: {},
  stack: [],
});

export function loadSequenceView(sequenceView: any): LoadedSequence {
  const { sequence = {}, sequenceDefaultTemplateSettings = [], offersSequencesFullDTO } = sequenceView ?? {};

  const sdtsByType: Record<string, any> = {};
  const templateValues: Record<string, any> = {};
  TEMPLATE_DEFAULTS.forEach(({ offerType, layoutField, containerField }) => {
    const sdts = sequenceDefaultTemplateSettings.find((s: any) => s?.offerType?.name === offerType) ?? {};
    sdtsByType[offerType] = sdts;
    templateValues[layoutField] = sdts.layout ?? null;
    templateValues[containerField] = sdts.container ?? null;
  });

  let rows = [...(offersSequencesFullDTO ?? [])]
    .filter(isPlacedRow)
    .sort((a: any, b: any) => a.orderBy - b.orderBy)
    .filter((os: any) => os.offer);

  let returnUrlOfferSequence: LoadedSequence['returnUrlOfferSequence'] = null;
  const last = rows[rows.length - 1];
  if (last && last.orderBy === RETURN_URL_ORDER_BY) {
    rows = rows.slice(0, -1);
    returnUrlOfferSequence = { id: last.id, orderBy: last.orderBy };
  }

  const stack: StackItem[] = rows.map((os: any, i: number) => ({
    key: `os-${os.id ?? i}-${i}`,
    offer: os.offer,
    // The classic editor only keeps a row's identity when it has a parent; a row without one is
    // re-sent as new. Kept as-is so both editors produce the same request.
    offersSequence: os.offersSequenceParent != null ? os : null,
    layoutOverride: os.isLayoutOverwritten && os.layout ? os.layout : null,
    containerOverride: os.isContainerOverwritten && os.container ? os.container : null,
  }));

  const values: Record<string, any> = {
    createdAt: sequence.createdAt || null,
    sequenceName: sequence.name || '',
    site: sequence.site || null,
    flex: sequence.flex || 1,
    priority: sequence.priority || 1,
    status: sequence.status || 0,
    returnRedirectDomain: sequence.returnRedirectDomain || null,
    returnVisits: sequence.returnVisits || 0,
    targetReturningUsers: sequence.targetReturningUsers || false,
    sequenceDescription: sequence.sequenceDescription || null,
    customVariables: sequence.customVariables ? JSON.parse(sequence.customVariables) : null,
    propertySetsIds: sequence.propertySetsIds || [],
    propertySetsValues: sequence.propertySetsValues || {},
    ...templateValues,
    ...(handleCriteriaInitialize(sequence.offerCriteria, null) ?? {}),
    stack,
  };
  if (sequence.criteriaConfiguration) {
    values.criteria = { criteriaConfiguration: JSON.stringify({ ...sequence.criteriaConfiguration }) };
  }

  return { values, sdtsByType, returnUrlOfferSequence, sequence };
}

export type SaveContext = {
  /** The id being edited; null when creating or copying. */
  sequenceId: number | null;
  /** Present when saving as a copy: the copy's name and site. */
  copy?: { name: string; site: { id: number; domain: string } } | null;
  isActive: boolean;
  /** Offer types keyed by name, as `offertypes` returns them. */
  offerTypesByName: Record<string, any>;
  sdtsByType: Record<string, any>;
  returnUrlOfferSequence: LoadedSequence['returnUrlOfferSequence'];
  /** The loaded sequence, for its criteria id and as the fallback `sequence` ref on saved rows. */
  sequence?: Record<string, any> | null;
};

const isUpdate = (ctx: SaveContext) => ctx.sequenceId != null && !ctx.copy;

export function buildSequence(values: Record<string, any>, ctx: SaveContext) {
  const customVariables = values.customVariables
    ? processCustomVariables(values.customVariables)
    : values.customVariables;
  let offerCriteria = handleCriteriaSubmit({ ...values, customVariables }, undefined, 'offerCriteria');
  if (isUpdate(ctx) && ctx.sequence?.offerCriteria?.id) {
    offerCriteria.id = ctx.sequence.offerCriteria.id;
  }
  let criteriaConfiguration = null;
  if (values.criteria) {
    const payload = buildCriteriaPayload(values, offerCriteria);
    criteriaConfiguration = payload.criteriaConfiguration;
    offerCriteria = payload.mergedOfferCriteria;
  }
  const site = ctx.copy ? ctx.copy.site : values.site;
  return {
    createdAt: values.createdAt || new Date(),
    endDate: null,
    flex: parseInt(values.flex, 10),
    priority: parseInt(values.priority, 10),
    isActive: ctx.copy ? false : ctx.isActive,
    isDynamic: false,
    name: ctx.copy ? ctx.copy.name : values.sequenceName,
    returnRedirectDomain: values.returnRedirectDomain || null,
    returnVisits: values.targetReturningUsers ? parseInt(values.returnVisits, 10) : 0,
    sequenceDescription: ctx.copy ? null : values.sequenceDescription || null,
    site: site ? { id: site.id, domain: site.domain } : site,
    startDate: null,
    status: 1,
    targetReturningUsers: values.targetReturningUsers || false,
    customVariables: customVariables ? JSON.stringify(processCustomVariables(customVariables)) : null,
    propertySetsIds: values?.propertySetsIds || [],
    propertySetsValues: values?.propertySetsValues || {},
    offerCriteria,
    criteriaConfiguration,
  };
}

export function buildTemplateSettings(values: Record<string, any>, ctx: SaveContext) {
  return TEMPLATE_DEFAULTS.map(({ offerType, layoutField, containerField }) => {
    const sdts: Record<string, any> = {
      container: toRef(values[containerField]),
      layout: toRef(values[layoutField]),
      offerType: ctx.offerTypesByName[offerType],
    };
    if (isUpdate(ctx)) {
      const saved = ctx.sdtsByType[offerType];
      if (saved?.id) sdts.id = saved.id;
      sdts.sequence = { id: ctx.sequenceId };
    }
    return sdts;
  });
}

/** The layout or container an offer renders with: its own override, else the sequence default for its type. */
export function resolveTemplate(item: StackItem, values: Record<string, any>, which: 'layout' | 'container') {
  const override = which === 'layout' ? item.layoutOverride : item.containerOverride;
  if (override) return { ref: toRef(override), overridden: true };
  const defaults = TEMPLATE_DEFAULTS.find((d) => d.offerType === item.offer?.offerType?.name);
  const fallback = defaults ? values[which === 'layout' ? defaults.layoutField : defaults.containerField] : null;
  return { ref: toRef(fallback), overridden: false };
}

const slimOffer = (offer: Offer | null | undefined, advertiser: any) =>
  offer ? { id: offer.id, name: offer.name, offerType: offer.offerType, isActive: offer.isActive, advertiser } : null;

export function buildOffersSequences(values: Record<string, any>, ctx: SaveContext) {
  const stack: StackItem[] = values.stack ?? [];
  const rows: Record<string, any>[] = stack.map((item, i) => {
    const layout = resolveTemplate(item, values, 'layout');
    const container = resolveTemplate(item, values, 'container');
    const saved = item.offersSequence;
    if (saved && !ctx.copy) {
      return {
        ...saved,
        orderBy: i + 1,
        container: container.ref,
        layout: layout.ref,
        isContainerOverwritten: container.overridden,
        isLayoutOverwritten: layout.overridden,
        offer: slimOffer(saved.offer, saved.offer?.advertiser),
        sequence: saved.sequence ? { id: saved.sequence.id } : ctx.sequence ? { id: ctx.sequence.id } : null,
        offersSequenceParent: saved.offersSequenceParent ? { id: saved.offersSequenceParent.id } : null,
      };
    }
    return {
      container: container.ref,
      layout: layout.ref,
      isContainerOverwritten: container.overridden,
      isLayoutOverwritten: layout.overridden,
      offer: slimOffer(item.offer, item.offer?.advertiser),
      orderBy: i + 1,
      status: 1,
    };
  });
  if (isUpdate(ctx) && ctx.returnUrlOfferSequence) rows.push(ctx.returnUrlOfferSequence);
  return rows;
}

export function buildSequenceView(values: Record<string, any>, ctx: SaveContext) {
  const sequence: Record<string, any> = buildSequence(values, ctx);
  if (isUpdate(ctx)) sequence.id = ctx.sequenceId;
  else sequence.createdAt = new Date();
  return {
    sequence,
    sequenceDefaultTemplateSettings: buildTemplateSettings(values, ctx),
    offersSequences: buildOffersSequences(values, ctx),
  };
}

/**
 * The published snapshot after an update. The save response's rows are matched back to what was
 * sent by position, picking up the ids and parents the backend assigned.
 */
export function buildUpdatePublish(
  values: Record<string, any>,
  sent: ReturnType<typeof buildSequenceView>,
  response: Record<string, any>,
  zuulUser: unknown
) {
  const sentRows = sent.offersSequences.map((row) => ({ ...row }));
  const offersSequences = (response.offersSequences ?? []).map((savedRow: any) => {
    const match = sentRows.find((row) => row.orderBy === savedRow.orderBy);
    if (savedRow.id && match) {
      match.id = savedRow.id;
      match.offersSequenceParent = savedRow.offersSequenceParent ? { id: savedRow.offersSequenceParent.id } : null;
    }
    return match;
  });
  const snapshot = JSON.stringify({
    ...response,
    sequence: sent.sequence,
    offersSequences,
    sequenceDefaultTemplateSettings: sent.sequenceDefaultTemplateSettings,
  });
  return {
    createdAt: values.createdAt,
    updatedAt: new Date(),
    sequence: sent.sequence,
    sequenceDraft: snapshot,
    sequencePublished: snapshot,
    zuulUser,
  };
}

export function buildCreatePublish(response: Record<string, any>, zuulUser: unknown) {
  return {
    createdAt: new Date(),
    updatedAt: new Date(),
    sequencePublished: JSON.stringify(response),
    sequence: response.sequence,
    zuulUser,
  };
}

/** The number of positions a row takes: a block takes one for itself and one per member offer. */
export const positionSpan = (offer: Offer) =>
  offer?.offerType?.id === OFFER_BLOCK_TYPE_ID ? 1 + (offer.offerBlock?.offerBlockOffers?.length ?? 0) : 1;
